<template>
    <div class="container">

        <div v-if="groupTraits">

            <spyscroller :sections="sections" />

            <!-- Section Summary -->
            <div
                id="section-summary"
                class="personality-section personality-section--summary-who-are-they personality-section--summary">

                <div>
                    <h2 class="personality-section--summary__title sr-only">{{ $t("discover-group-profile") }}</h2>
                    <ul
                        class="personality-section__anchor-list personality-section__anchor-list--5">
                        <li
                            v-for="(item, index) in sections"
                            :key="`section-personality-${index}`"
                            v-if="item.visibleOnSummary"
                            class="personality-section__anchor-list-item">
                            <button
                                type="button"
                                class="button button--transparent circle-anchor"
                                @click="jumpToId(`section-${item.name}`)">
                                {{ $t(item.name) }}
                            </button>
                        </li>
                    </ul>

                    <div class="personality-section__artwork-gender">
                        <template v-if="loggedUser.gender === 'M'">
                            <img
                                class="personality-section__artwork-gender-background"
                                width="224"
                                height="140"
                                src="@/assets/images/artworks/gender/group-men.svg"
                                :alt="$t('man')" />
                        </template>
                        <template v-else>
                            <img
                                width="224"
                                height="140"
                                src="@/assets/images/artworks/gender/group-women.svg"
                                :alt="$t('woman')" />
                        </template>
                        <div
                            class="vertical-sep vertical-sep--summary"
                            :style="`height: ${summaryArtworkVerticalSepHeight}px;`"
                        ></div>
                    </div>
                </div>

                <img
                    class="personality-section__artwork-mouse"
                    width="27"
                    height="62"
                    src="@/assets/images/artworks/who-i-am/mouse-down.svg"
                    alt="" />
            </div>


            <!-- Section Domains -->
            <!--  <personality-friends :data="userFriends" /> -->
            
            <!-- Section Elements -->
            <personality-elements
                v-if="groupElements"
                :data="groupElements"
                type="group"
                class="personality-section--fluid"
                @open-section-help="openSectionHelpModal">

                <div class="personality__subsection">
                    <h3 class="personality-subsection__title">{{ $t("the-most-fire-member") }}</h3>
                    <gauge
                        v-if="isNullOrPositiveNumber(userElementFire) && groupElementDetailFire"
                        :gender="loggedUser.gender"
                        :user-data="userElementFire"
                        :data="groupElementDetailFire" />
                </div>

                <div class="personality__subsection">
                    <h3 class="personality-subsection__title">{{ $t("the-most-earth-member") }}</h3>
                    <gauge
                        v-if="isNullOrPositiveNumber(userElementEarth) && groupElementDetailEarth"
                        :gender="loggedUser.gender"
                        :user-data="userElementEarth"
                        :data="groupElementDetailEarth" />
                </div>

                <div class="personality__subsection">
                    <h3 class="personality-subsection__title">{{ $t("the-most-air-member") }}</h3>
                    <gauge
                        v-if="isNullOrPositiveNumber(userElementAir)&& groupElementDetailAir"
                        :gender="loggedUser.gender"
                        :user-data="userElementAir"
                        :data="groupElementDetailAir" />
                </div>

                <div class="personality__subsection">
                    <h3 class="personality-subsection__title">{{ $t("the-most-water-member") }}</h3>
                    <gauge
                        v-if="isNullOrPositiveNumber(userElementWater) && groupElementDetailWater"
                        :gender="loggedUser.gender"
                        :user-data="userElementWater"
                        :data="groupElementDetailWater" />
                </div>

            </personality-elements>

            <!-- Sections Polarity -->
            <personality-polarity
                v-if="groupPolarity"
                :data="groupPolarity"
                type="group"
                class="personality-section--fluid"
                @open-section-help="openSectionHelpModal">

                <div
                    v-if="isNullOrPositiveNumber(userPolarityF) && groupPolarityDetailFeminine"
                    class="personality__subsection">
                    <h3 class="personality-subsection__title">{{ $t("the-most-feminine-member") }}</h3>
                    <gauge
                        :gender="loggedUser.gender"
                        :user-data="userPolarityF"
                        :data="groupPolarityDetailFeminine" />
                </div>

                <div
                    v-if="isNullOrPositiveNumber(userPolarityM) && groupPolarityDetailMasculine"
                    class="personality__subsection">
                    <h3 class="personality-subsection__title">{{ $t("the-most-masculine-member") }}</h3>
                    <gauge
                        :gender="loggedUser.gender"
                        :user-data="userPolarityM"
                        :data="groupPolarityDetailMasculine" />
                </div>
            </personality-polarity>


            <!-- Sections Autonomy -->
            <personality-autonomy
                v-if="groupAutonomy"
                :data="groupAutonomy"
                :gender="loggedUser.gender"
                type="group"
                class="personality-section--fluid"
                @open-section-help="openSectionHelpModal" >

                <div
                    v-if="isNullOrPositiveNumber(userAutonomyOriental) && groupAutonomyDetailOriental"
                    class="personality__subsection">
                    <h3 class="personality-subsection__title">{{ $t("the-most-oriental-member") }}</h3>
                    <gauge
                        :gender="loggedUser.gender"
                        :user-data="userAutonomyOriental"
                        :data="groupAutonomyDetailOriental" />
                </div>

                <div
                    v-if="isNullOrPositiveNumber(userAutonomyOccidental) && groupAutonomyDetailOccidental"
                    class="personality__subsection">
                    <h3 class="personality-subsection__title">{{ $t("the-most-occidental-member") }}</h3>
                    <gauge
                        :gender="loggedUser.gender"
                        :user-data="userAutonomyOccidental"
                        :data="groupAutonomyDetailOccidental" />
                </div>

            </personality-autonomy>

            <!-- Sections Extraversion -->
            <personality-extraversion
                v-if="groupExtraversion"
                :data="groupExtraversion"
                :gender="loggedUser.gender"
                type="group"
                class="personality-section--fluid"
                @open-section-help="openSectionHelpModal">

                <div
                    v-if="isNullOrPositiveNumber(userExtraversionIntravert) && groupExtraversionDetailIntravert"
                    class="personality__subsection">
                    <h3 class="personality-subsection__title">{{ $t("the-most-intravert-member") }}</h3>
                    <gauge
                        :gender="loggedUser.gender"
                        :user-data="userExtraversionIntravert"
                        :data="groupExtraversionDetailIntravert" />
                </div>

                <div
                    v-if="isNullOrPositiveNumber(userExtraversionExtravert) && groupExtraversionDetailExtravert"
                    class="personality__subsection">
                    <h3 class="personality-subsection__title">{{ $t("the-most-extravert-member") }}</h3>
                    <gauge
                        :gender="loggedUser.gender"
                        :user-data="userExtraversionExtravert"
                        :data="groupExtraversionDetailExtravert" />
                </div>

            </personality-extraversion>

            <!-- Sections Extraversion -->
            <personality-modality
                v-if="groupModality"
                :data="groupModality"
                :gender="loggedUser.gender"
                type="group"
                class="personality-section--fluid"
                @open-section-help="openSectionHelpModal">

                <div
                    v-if="isNullOrPositiveNumber(userModalityBeginner) && groupModalityDetailBeginner"
                    class="personality__subsection">
                    <h3 class="personality-subsection__title">{{ $t("the-most-beginner-member") }}</h3>
                    <gauge
                        :gender="loggedUser.gender"
                        :user-data="userModalityBeginner"
                        :data="groupModalityDetailBeginner" />
                </div>

                <div
                    v-if="isNullOrPositiveNumber(userModalityContinuator) && groupModalityDetailContinuator"
                    class="personality__subsection">
                    <h3 class="personality-subsection__title">{{ $t("the-most-continuator-member") }}</h3>
                    <gauge
                        :gender="loggedUser.gender"
                        :user-data="userModalityContinuator"
                        :data="groupModalityDetailContinuator" />
                </div>

                <div
                    v-if="isNullOrPositiveNumber(userModalityCoordinator) && groupModalityDetailCoordinator"
                    class="personality__subsection">
                    <h3 class="personality-subsection__title">{{ $t("the-most-coordinator-member") }}</h3>
                    <gauge
                        :gender="loggedUser.gender"
                        :user-data="userModalityCoordinator"
                        :data="groupModalityDetailCoordinator" />
                </div>

                

            </personality-modality>


            <div
                id="section-outro"
                class="personality-section personality-section--outro">
                <div class="vertical-sep vertical-sep--top"></div>

                <h2 class="personality-section__title">{{ $t("compare-with-other") }}</h2>

                <div class="personality-section__content">
                    <ul class="buttons-list buttons-list--centered">
                        <li class="buttons-list__item">
                            <button
                                type="button"
                                @click="$emit('reset-partner')"
                                class="button button--primary">
                                <span class="button__label">{{ $t("change-group") }}</span>
                            </button>
                        </li>
                        <li
                            class="buttons-list__item">
                            <button
                                type="button"
                                @click="jumpToId('app')"
                                class="button button--primary">
                                {{ $t("go-to-match") }}
                            </button>
                        </li>
                    </ul>
                </div>
            </div>

            <personality-section-know-more-modal
                v-if="isSectionHelpModalVisible"
                :data="currentKnowMoreModalData"
                @closeModal="closeSectionHelpModal"></personality-section-know-more-modal>

            <trait-detail-modal
                v-if="istraitModalVisible"
                :current-trait-detail="currentTraitModalData"
                :display-score="displayScore"
                @closeModal="closeTraitDetailModal"
                :current-user-score="currentUserScore">
            </trait-detail-modal>


            <transition name="bubble-modal">
                <bubble-mobile-modal
                    v-if="isMobileBubbleModalOpen"
                    :data="currentBubble"
                    @closeModal="closeMobileBubbleModal"></bubble-mobile-modal>
            </transition>
        </div>
        <div v-else>
            <div
                v-if="fetchGroupTraitsTentative <= 5"
                class="loader">
                <HorizontalBubbleLoader />
            </div>
            <div v-else>
                <div class="container">
                    <p>{{ $t("service-temporarily-unavailable") }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
// @ is an alias to /src
import axios from "axios";

import PersonalitySectionKnowMoreModal from "@/components/modals/personality/PersonalitySectionKnowMoreModal.vue";

import Spyscroller from "@/components/spyscroller/Spyscroller.vue";
import PersonalityElements from "@/components/personality/sections/PersonalityElements.vue";
import PersonalityPolarity from "@/components/personality/sections/PersonalityPolarity.vue";
import PersonalityAutonomy from "@/components/personality/sections/PersonalityAutonomy.vue";
import PersonalityExtraversion from "@/components/personality/sections/PersonalityExtraversion.vue";
import PersonalityModality from "@/components/personality/sections/PersonalityModality.vue";
//import PersonalityFriends from "@/components/personality/sections/PersonalityFriends.vue";

import BubbleMobileModal from "@/components/modals/BubbleMobileModal.vue";

//import FieldSelect from "@/components/forms/FieldSelect.vue";
import Gauge from "@/components/charts/Gauge.vue";

import { getThem } from "@/utils/api/groupsApi.js";

import HorizontalBubbleLoader from "@/components/loaders/HorizontalBubbleLoader.vue";

export default {
    name: "WhoAreThey",
    components: {
        PersonalitySectionKnowMoreModal: PersonalitySectionKnowMoreModal,
        Spyscroller: Spyscroller,
        PersonalityElements: PersonalityElements,
        PersonalityPolarity: PersonalityPolarity,
        PersonalityAutonomy: PersonalityAutonomy,
        PersonalityExtraversion: PersonalityExtraversion,
        PersonalityModality: PersonalityModality,
        BubbleMobileModal: BubbleMobileModal,
        //PersonalityFriends : PersonalityFriends,
        // FieldSelect: FieldSelect,
        HorizontalBubbleLoader: HorizontalBubbleLoader,
        Gauge: Gauge
    },
    props: {
        groupId: {
            type: Number,
            required: true
        }
    },
    data () {
        return {
            groupTraits: null,
            fetchGroupTraitsTentative: 0,
            isMobile: true,
            isMobileBubbleModalOpen: false,
            currentBubble: {},
            currentUserScore: 0,
            displayScore: "",
            currentTrait: "",
            traitsData: "",
            currentTraitModalData: "",
            istraitModalVisible: false,
            knowMoreData: "",
            currentKnowMoreModalData: "",
            isSectionHelpModalVisible: false,
            viewPortHeight: window.innerHeight,
            sections: [
                {
                    "name": "summary",
                    "visibleOnSummary": true
                },
                {
                    "name": "elements",
                    "visibleOnSummary": true
                },
                {
                    "name": "polarity",
                    "visibleOnSummary": true
                },
                {
                    "name": "autonomy",
                    "visibleOnSummary": true
                },
                {
                    "name": "extraversion",
                    "visibleOnSummary": true
                },
                {
                    "name": "modality",
                    "visibleOnSummary": true
                },
                {
                    "name": "outro",
                    "visibleOnSummary": true
                }
            ],
        };
    },
    computed: {
        // loggedUser
        // Current logged loggedUser
        loggedUser() {
            return this.$store.getters["userModule/getUser"];
        },
        userPersonality() {
            return this.$store.getters["userModule/getPersonality"].part1;
        },
        /*userFriends() {
            if (this.userPersonality) {
                const domains = this.userPersonality.filter((item) => {
                    return (item.category === "Sun");
                });

                return domains;
            }

            return;
        },*/
        userPolarityM() {
            if (this.userPersonality) {
                return this.getUserTraitValue("Gender", "M");
            }

            return;
        },
        userPolarityF() {
            if (this.userPersonality) {
                return this.getUserTraitValue("Gender", "F");
            }

            return;
        },
        groupPolarity() {
            if (this.groupTraits) {
                const polarity = this.groupTraits.filter((item) => {
                    return (item.report_name === "groupMean" && item.category === "Gender");
                });

                return polarity;
            }

            return;
        },
        groupPolarityDetailMasculine() {
            if (this.groupTraits) {
                return this.getGroupeDetailData("Gender", "M");
            }

            return;
        },
        groupPolarityDetailFeminine() {
            if (this.groupTraits) {
                return this.getGroupeDetailData("Gender", "F");
            }

            return;
        },
        groupElements() {
            if (this.groupTraits) {
                const elements = this.groupTraits.filter((item) => {
                    return (item.report_name === "groupMean" && item.category === "Elements");
                });

                return elements;
            }

            return;
        },
        groupElementDetailFire() {
            if (this.groupTraits) {
                return this.getGroupeDetailData("Elements", "feu");
            }

            return;
        },
        groupElementDetailEarth() {
            if (this.groupTraits) {
                return this.getGroupeDetailData("Elements", "terre");
            }

            return;
        },
        groupElementDetailAir() {
            if (this.groupTraits) {
                return this.getGroupeDetailData("Elements", "air");
            }

            return;
        },
        groupElementDetailWater() {
            if (this.groupTraits) {
                return this.getGroupeDetailData("Elements", "eau");
            }

            return;
        },
        userElementFire() {
            if (this.userPersonality) {
                return this.getUserTraitValue("Elements", "feu");
            }


            return;
        },
        userElementEarth() {
            if (this.userPersonality) {
                return this.getUserTraitValue("Elements", "terre");
            }

            return;
        },
        userElementAir() {
            if (this.userPersonality) {
                return this.getUserTraitValue("Elements", "air");
            }

            return;
        },
        userElementWater() {
            if (this.userPersonality) {
                return this.getUserTraitValue("Elements", "eau");
            }

            return;
        },
        groupAutonomy() {
            if (this.groupTraits) {
                const autonomy = this.groupTraits.filter((item) => {
                    return (item.report_name === "groupMean" && item.category === "Secteur");
                });

                return autonomy;
            }

            return;
        },
        groupAutonomyDetailOriental() {
            if (this.groupTraits) {
                return this.getGroupeDetailData("Secteur", "oriental");
            }

            return;
        },
        groupAutonomyDetailOccidental() {
            if (this.groupTraits) {
                return this.getGroupeDetailData("Secteur", "occidental");
            }

            return;
        },
        userAutonomyOriental() {
            if (this.userPersonality) {
                return this.getUserTraitValue("Secteur", "oriental");
            }

            return;
        },
        userAutonomyOccidental() {
            if (this.userPersonality) {
                return this.getUserTraitValue("Secteur", "occidental");
            }

            return;
        },
        groupExtraversion() {
            if (this.groupTraits) {
                const extraversion = this.groupTraits.filter((item) => {
                    return (item.report_name === "groupMean" && item.category === "SecteurDay");
                });

                return extraversion;
            }

            return;
        },
        groupExtraversionDetailIntravert() {
            if (this.groupTraits) {
                return this.getGroupeDetailData("SecteurDay", "A-W");
            }

            return;
        },
        groupExtraversionDetailExtravert() {
            if (this.groupTraits) {
                return this.getGroupeDetailData("SecteurDay", "S-S");
            }

            return;
        },
        userExtraversionIntravert() {
            if (this.userPersonality) {
                return this.getUserTraitValue("SecteurDay", "A-W");
            }

            return;
        },
        userExtraversionExtravert() {
            if (this.userPersonality) {
                return this.getUserTraitValue("SecteurDay", "S-S");
            }

            return;
        },
        groupModality() {
            if (this.groupTraits) {
                const modality = this.groupTraits.filter((item) => {
                    return (item.category === "Ternaire");
                });

                return modality;
            }

            return;
        },
        groupModalityDetailBeginner() {
            if (this.groupTraits) {
                return this.getGroupeDetailData("Ternaire", "Cardinal");
            }

            return;
        },
        groupModalityDetailCoordinator() {
            if (this.groupTraits) {
                return this.getGroupeDetailData("Ternaire", "Mutable");
            }

            return;
        },
        groupModalityDetailContinuator() {
            if (this.groupTraits) {
                return this.getGroupeDetailData("Ternaire", "Fixe");
            }

            return;
        },
        userModalityBeginner() {
            if (this.userPersonality) {
                return this.getUserTraitValue("Ternaire", "Cardinal");
            }

            return;
        },
        userModalityCoordinator() {
            if (this.userPersonality) {
                return this.getUserTraitValue("Ternaire", "Mutable");
            }

            return;
        },
        userModalityContinuator() {
            if (this.userPersonality) {
                return this.getUserTraitValue("Ternaire", "Fixe");
            }

            return;
        },
        summaryArtworkVerticalSepHeight() {
            let height = (this.viewPortHeight / 2) - 225;

            if (!this.isMobile) {
                height = (this.viewPortHeight / 2) - 460;
            }

            if (height < 120 && !this.isMobile) {
                return 120;
            }

            return height;
        }
    },
    watch: {
        groupId(val, oldVal) {
            if (val !== oldVal) {
                this.fetchGroupTrait();
            }
        }
    },
    mounted() {
        // Responsive Behavior
        /* istanbul ignore next */
        this.$responsive.registerMediaQuery("from-desktop", () => {
            this.isMobile = false;
        }, () => {
            this.isMobile = true;
        });

        window.addEventListener("resize", this.getWiewportHeight);

        this.fetchKnowMoreData();

        // Insight Traits
        if (!this.groupTraits) {
            this.fetchGroupTrait();
        }

        this.isPageReady = true;
    },
    destroyed() {
        document.removeEventListener("resize", this.getWiewportHeight);
    },
    methods: {
        // updateCurrentGroup(value) {
        //     this.currentGroupId = parseInt(value);
        //     this.fetchGroupTrait();
        // },
        fetchKnowMoreData () {

            const currentLocal = this.$i18n.locale;

            axios({
                url: `/json/personality/personality-know-more-content.${currentLocal}.json`,
            }).then(res => {
                this.knowMoreData = res.data;
            }).catch(e => {
                if (e.response) {
                    throw e.response;
                }
            });
        },
        openSectionHelpModal(section) {
            this.currentKnowMoreModalData = this.knowMoreData[section];
            this.isSectionHelpModalVisible = true;
        },
        closeSectionHelpModal() {
            this.isSectionHelpModalVisible = false;
        },
        updateCurrentCategory(type) {
            this.currentCategory = type;
        },
        getWiewportHeight() {
            this.viewPortHeight = window.innerHeight;
        },
        openMobileBubbleModal(bubble) {
            // if (this.isMobile) {
            //     this.currentBubble = bubble;
            //     this.isMobileBubbleModalOpen = true;
            // }
        },
        closeMobileBubbleModal() {
            // this.currentBubble = {};
            // this.isMobileBubbleModalOpen = false;
        },
        fetchGroupTrait(id) {
            this.groupTraits = null;
            this.fetchGroupTraitsTentative += 1;

            const group = {
                "id": this.groupId
            };

            getThem(group).then((res) => {
                if (res.status === 200 && res.data) {
                    this.groupTraits = res.data.part1;
                } else {
                    // If res.data is empty, it maybe bcause the API is still processing data (after a getMeCircleFirstTime call), try again to fetch data.
                    if (this.fetchGroupTraitsTentative <= 5 && (!res.data || (res.data && res.data.length === 0))) {
                        this.fetchGroupTrait();
                    } else {
                        // Handle Error
                        let message = this.$t("a-server-error-occured-please-try-later");
                        this.$notificationsBar.alert(message, {type: "warning"});
                    }
                }
            });
        },
        getGroupeDetailData(category, trait) {
            const polarityDetail = this.groupTraits.filter((item) => {
                return (item.report_name === "groupDetail" && item.category === category && item.traits === trait);
            });

            let peoples = polarityDetail.reduce((peoplesArray, trait) => {
                const id = trait["id_person"];

                if (!peoplesArray[id]) {
                    peoplesArray[id] = {
                        "id": id,
                        "x": trait.points_perc,
                        "y": trait.y,
                        "label": trait.name
                    };
                }

                return peoplesArray;
            }, {});

            const cleanedPeoples = Object.values(peoples);

            return cleanedPeoples;
        },
        getUserTraitValue(category, trait) {
            const secteur = this.userPersonality.filter((item) => {
                return (item.type === "QQ" && item.category === category);
            });

            var index = this.getObjetIndexByKey(secteur, "traits", trait);
            if (index !== -1) {
                return secteur[index].points_perc;
            }
        }
    }
};
</script>

